// src/lib/questionnaire/hrvSelfV1.ts
import hrvRaw from '@/configs/questionnaires/hrv/v1/hrv_self_v1.json';

export type HrvDomainCode = 'OG' | 'OC' | 'SC' | 'DS';

export type HrvExposureLabel = 'Low Exposure' | 'Moderate Exposure' | 'High Exposure';

export type HrvEscalationReasonCode =
  | 'HIGH_COMPOSITE'
  | 'OWNERSHIP_DATA_COMBINATION'
  | 'CONTROL_SUPPLY_COMBINATION'
  | null;

export interface HrvDomainResult {
  domain: HrvDomainCode;
  label: string;
  score: number;
  exposure_label: HrvExposureLabel;
}

export interface HrvAssessmentSummary {
  composite_score: number;
  composite_exposure_label: HrvExposureLabel;
  escalation_required: boolean;
  escalation_reason_code: HrvEscalationReasonCode;
  domain_scores: HrvDomainResult[];
}

export const HRV_DOMAIN_LABELS: Record<HrvDomainCode, string> = {
  OG: 'Ownership & Governance Exposure',
  OC: 'Operational Control & Access Risk',
  SC: 'Supply Chain & Technology Composition',
  DS: 'Data & Workload Sensitivity',
};

export const HRV_DOMAIN_ORDER: HrvDomainCode[] = ['OG', 'OC', 'SC', 'DS'];

export const HRV_DOMAIN_WEIGHTS: Record<HrvDomainCode, number> = {
  OG: 30,
  OC: 25,
  SC: 25,
  DS: 20,
};

const isDev = import.meta.env.DEV;

/** Map numeric score → exposure label using defined thresholds */
export function hrvExposureLabel(score: number): HrvExposureLabel {
  if (score < 1.50) return 'Low Exposure';
  if (score < 2.25) return 'Moderate Exposure';
  return 'High Exposure';
}

/** Legacy alias — maps to simple risk wording */
export function hrvRiskLabel(score: number): string {
  if (score <= 1.5) return 'Low Risk';
  if (score <= 2.5) return 'Moderate Risk';
  return 'High Risk';
}

/**
 * Compute weighted HRV scores with escalation logic.
 * Input: array of { domain, score } where score is 1|2|3.
 */
export function computeHrvScores(
  answers: Array<{ domain: string; score: number }>
): HrvAssessmentSummary {
  const sums: Record<string, number> = {};
  const counts: Record<string, number> = {};

  for (const a of answers) {
    if (!a.score || !HRV_DOMAIN_ORDER.includes(a.domain as HrvDomainCode)) continue;
    sums[a.domain] = (sums[a.domain] ?? 0) + a.score;
    counts[a.domain] = (counts[a.domain] ?? 0) + 1;
  }

  const byDomain = {} as Record<HrvDomainCode, number>;
  let compositeNum = 0;
  let compositeDen = 0;

  for (const d of HRV_DOMAIN_ORDER) {
    const avg = counts[d] ? sums[d] / counts[d] : 0;
    byDomain[d] = Math.round(avg * 100) / 100;
    compositeNum += avg * HRV_DOMAIN_WEIGHTS[d];
    compositeDen += HRV_DOMAIN_WEIGHTS[d];
  }

  const composite_score = compositeDen > 0
    ? Math.round((compositeNum / compositeDen) * 100) / 100
    : 0;

  const composite_exposure_label = hrvExposureLabel(composite_score);

  // Build domain results
  const domain_scores: HrvDomainResult[] = HRV_DOMAIN_ORDER.map(d => ({
    domain: d,
    label: HRV_DOMAIN_LABELS[d],
    score: byDomain[d],
    exposure_label: hrvExposureLabel(byDomain[d]),
  }));

  // Escalation logic
  const ogExposure = hrvExposureLabel(byDomain.OG);
  const ocExposure = hrvExposureLabel(byDomain.OC);
  const scExposure = hrvExposureLabel(byDomain.SC);
  const dsExposure = hrvExposureLabel(byDomain.DS);

  let escalation_required = false;
  let escalation_reason_code: HrvEscalationReasonCode = null;

  // A) Composite = High
  if (composite_exposure_label === 'High Exposure') {
    escalation_required = true;
    escalation_reason_code = 'HIGH_COMPOSITE';
  }
  // B) OG = High AND DS = Moderate or High
  else if (ogExposure === 'High Exposure' && (dsExposure === 'Moderate Exposure' || dsExposure === 'High Exposure')) {
    escalation_required = true;
    escalation_reason_code = 'OWNERSHIP_DATA_COMBINATION';
  }
  // C) OC = High AND SC = High
  else if (ocExposure === 'High Exposure' && scExposure === 'High Exposure') {
    escalation_required = true;
    escalation_reason_code = 'CONTROL_SUPPLY_COMBINATION';
  }

  // Dev logging
  if (isDev) {
    console.log('[HRV Scoring] domain scores:', byDomain);
    console.log('[HRV Scoring] composite score:', composite_score, '→', composite_exposure_label);
    console.log('[HRV Scoring] escalation_required:', escalation_required, 'reason:', escalation_reason_code);
  }

  return {
    composite_score,
    composite_exposure_label,
    escalation_required,
    escalation_reason_code,
    domain_scores,
  };
}

/** Get the raw JSON config */
export function getHrvConfig() {
  return hrvRaw as typeof hrvRaw;
}
